import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination, { ListSettingsUpdate } from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmDealerGroupFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { ListSettings } from "@dashboard/types";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { groupMessages, sectionMessages } from "../../messages";
import { wsmDealerGroupAddPath, wsmDealerGroupUrl } from "../../urls";
import WsmDealerTabs from "../WsmDealerTabs/WsmDealerTabs";

const numberOfColumns = 4;

export interface WsmDealerGroupListPageProps {
  groups: WsmDealerGroupFragment[] | undefined;
  query: string;
  settings: ListSettings;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onQueryChange: (query: string) => void;
  onUpdateListSettings: ListSettingsUpdate;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const WsmDealerGroupListPage = ({
  groups,
  query,
  settings,
  hasNextPage,
  hasPreviousPage,
  onQueryChange,
  onUpdateListSettings,
  onNextPage,
  onPreviousPage,
}: WsmDealerGroupListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.groupListTitle)}>
        <Button onClick={() => navigate(wsmDealerGroupAddPath)} data-test-id="wsm-dealer-list-create">
          {intl.formatMessage(groupMessages.createGroup)}
        </Button>
      </TopNav>
      <Box overflowY="auto" height="100%">
        <WsmDealerTabs current="groups" />
        <Box paddingTop={5}>
          <DashboardCard data-test-id="wsm-dealer-group-list">
            <DashboardCard.Content paddingX={0}>
              <Box paddingTop={4} paddingBottom={5}>
                <SearchInput
                  initialSearch={query}
                  placeholder={intl.formatMessage(groupMessages.searchPlaceholder)}
                  onSearchChange={onQueryChange}
                />
              </Box>
              <ResponsiveTable>
                <TableHead>
                  <TableRowLink>
                    <TableCell>{intl.formatMessage(groupMessages.colCode)}</TableCell>
                    <TableCell>{intl.formatMessage(groupMessages.colName)}</TableCell>
                    <TableCell>{intl.formatMessage(groupMessages.colCustomers)}</TableCell>
                    <TableCell>{intl.formatMessage(groupMessages.colTierPrices)}</TableCell>
                  </TableRowLink>
                </TableHead>
                <TableBody>
                  {renderCollection(
                    groups,
                    group => (
                      <TableRowLink
                        key={group ? group.id : "skeleton"}
                        hover={!!group}
                        href={group ? wsmDealerGroupUrl(group.id) : undefined}
                        data-test-id="wsm-dealer-group-row"
                      >
                        <TableCell>{group?.code ?? <Skeleton />}</TableCell>
                        <TableCell>{group ? group.name || "-" : <Skeleton />}</TableCell>
                        <TableCell>{group ? group.customerCount : <Skeleton />}</TableCell>
                        <TableCell>{group ? group.tierPriceCount : <Skeleton />}</TableCell>
                      </TableRowLink>
                    ),
                    () => (
                      <TableRowLink data-test-id="wsm-dealer-group-list-empty">
                        <TableCell colSpan={numberOfColumns}>
                          <Box paddingY={6}>{intl.formatMessage(groupMessages.noGroups)}</Box>
                        </TableCell>
                      </TableRowLink>
                    ),
                  )}
                </TableBody>
                <TableFooter>
                  <TableRowLink>
                    <TablePagination
                      colSpan={numberOfColumns}
                      settings={settings}
                      hasNextPage={hasNextPage}
                      hasPreviousPage={hasPreviousPage}
                      onUpdateListSettings={onUpdateListSettings}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                    />
                  </TableRowLink>
                </TableFooter>
              </ResponsiveTable>
            </DashboardCard.Content>
          </DashboardCard>
        </Box>
      </Box>
    </ListPageLayout>
  );
};

export default WsmDealerGroupListPage;
